import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#08121F] text-white">
      <Navigation />

      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-alert-line text-red-400 text-sm"></i>
              <span className="text-xs text-red-400 uppercase tracking-widest font-semibold">Legal Document</span>
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              WingmanFix Terms of Use<br />
              <span className="text-[#00FFD1]">and Safety Disclaimer</span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed">
              <strong>Effective Date:</strong> January 1, 2025
            </p>
          </div>

          {/* Intro */}
          <div className="bg-[#0D1B2E] border border-[#00FFD1]/20 rounded-2xl p-8 mb-8">
            <p className="text-base text-gray-300 leading-relaxed">
              These Terms of Use and Safety Disclaimer ("Terms") govern your access to and use of the WingmanFix website, mobile application, and related services (collectively, the "Service"). By accessing or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you must not use the Service.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-10">
            {/* Section 1 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00FFD1]/10 border border-[#00FFD1]/30 flex-shrink-0">
                  <i className="ri-information-line text-[#00FFD1] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">1. Purpose of the Service</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      WingmanFix is a pilot training, flight review, and situational awareness tool designed to assist pilots in learning, reviewing flight performance, and accessing aircraft reference information. The Service provides features including but not limited to flight data analysis, audio transcription, performance scoring, training insights, and aircraft reference materials.
                    </p>
                    <p>
                      <strong className="text-white">The Service is intended for educational and advisory purposes only.</strong> It is designed to supplement, not replace, proper pilot training, aircraft documentation, and certified aviation systems.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/10 border border-red-500/30 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">2. Not a Certified Aviation System</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      <strong className="text-white">WingmanFix is not certified avionics equipment.</strong> The Service has not been approved, certified, or endorsed by the Federal Aviation Administration (FAA), European Union Aviation Safety Agency (EASA), or any other aviation regulatory authority.
                    </p>
                    <p>
                      The application must not be relied upon as a primary flight instrument, navigation system, or operational system. WingmanFix does not meet the standards required for certified aviation equipment and must not be used as a substitute for approved aircraft systems or instruments.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#B4FF39]/10 border border-[#B4FF39]/30 flex-shrink-0">
                  <i className="ri-shield-user-line text-[#B4FF39] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">3. Pilot in Command Responsibility</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      <strong className="text-white">The pilot in command (PIC) is solely and exclusively responsible for the safe operation of the aircraft and compliance with all applicable aviation regulations.</strong> This responsibility includes, but is not limited to:
                    </p>
                    <ul className="list-none space-y-2 ml-6">
                      {[
                        'Aircraft operation and control',
                        'Navigation and flight planning',
                        'Weather evaluation and go/no-go decisions',
                        'Air traffic control communications and compliance',
                        'Performance calculations and limitations',
                        'Weight and balance determinations',
                        'Emergency procedures and decision-making',
                        'Compliance with Federal Aviation Regulations (FARs) and all applicable laws',
                        'Ensuring aircraft airworthiness',
                        'Passenger safety and briefings'
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <i className="ri-arrow-right-s-line text-[#B4FF39] text-lg flex-shrink-0 mt-0.5"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="pt-2">
                      <strong className="text-white">WingmanFix does not assume operational control, decision-making authority, or responsibility for flight operations.</strong> The Service is a tool only; all operational decisions remain with the pilot in command.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00A3E0]/10 border border-[#00A3E0]/30 flex-shrink-0">
                  <i className="ri-file-info-line text-[#00A3E0] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">4. Advisory Information Only</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      Any suggestions, summaries, calculations, checklists, performance data, weather information, or other information provided by WingmanFix are <strong className="text-white">advisory in nature only</strong> and may be incomplete, inaccurate, outdated, or incorrect.
                    </p>
                    <p>
                      Pilots must always verify all operational information using approved sources, including but not limited to:
                    </p>
                    <ul className="list-none space-y-2 ml-6">
                      {[
                        'FAA-approved Aircraft Flight Manual (AFM)',
                        'Pilot Operating Handbook (POH)',
                        'Certified aircraft instruments and avionics',
                        'Official aeronautical charts and publications',
                        'Current NOTAMs and weather briefings',
                        'Air traffic control instructions'
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-line text-[#00A3E0] text-lg flex-shrink-0 mt-0.5"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="pt-2">
                      <strong className="text-white">Never rely solely on WingmanFix for operational decisions.</strong>
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/10 border border-red-500/30 flex-shrink-0">
                  <i className="ri-alarm-warning-line text-red-400 text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">5. Emergency Procedures Disclaimer</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      Emergency checklists, abnormal procedures, or aircraft reference information displayed by WingmanFix are provided <strong className="text-white">only as training or reference material</strong> and must not be used during actual flight operations or emergencies.
                    </p>
                    <p>
                      <strong className="text-white">In all cases, pilots must follow the procedures published in the FAA-approved Aircraft Flight Manual (AFM) or Pilot Operating Handbook (POH) for the specific aircraft being operated.</strong> Emergency procedures vary by aircraft type, configuration, and operating conditions. WingmanFix cannot account for all variables and must not be relied upon in emergency situations.
                    </p>
                    <p>
                      Pilots are responsible for maintaining current knowledge of emergency procedures through proper training, recurrent practice, and reference to approved aircraft documentation.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00FFD1]/10 border border-[#00FFD1]/30 flex-shrink-0">
                  <i className="ri-robot-line text-[#00FFD1] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">6. AI and Automated Systems Disclaimer</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      WingmanFix may use artificial intelligence (AI), machine learning models, natural language processing, or other automated systems to analyze flight data, transcribe audio communications, generate performance insights, parse ATC clearances, or provide training recommendations.
                    </p>
                    <p>
                      <strong className="text-white">Automated outputs may contain errors, misinterpretations, or inaccuracies.</strong> AI systems are not perfect and may fail to correctly interpret audio, data, or operational context. Users must not rely on automated outputs as the sole basis for operational decisions.
                    </p>
                    <p>
                      All AI-generated information, including but not limited to clearance readbacks, performance scores, and training insights, must be independently verified by the pilot before use.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#B4FF39]/10 border border-[#B4FF39]/30 flex-shrink-0">
                  <i className="ri-mic-line text-[#B4FF39] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">7. Data Recording and Audio Processing</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      WingmanFix may capture, record, or process cockpit audio, air traffic control communications, flight parameters, GPS data, and other operational data to generate training insights, performance summaries, and flight reviews.
                    </p>
                    <p>
                      <strong className="text-white">Users are solely responsible for complying with all applicable laws and regulations regarding audio recording, data collection, and privacy.</strong> This includes but is not limited to:
                    </p>
                    <ul className="list-none space-y-2 ml-6">
                      {[
                        'Federal and state wiretapping or recording consent laws',
                        'FCC regulations regarding radio communications',
                        'Privacy rights of passengers and other individuals',
                        'Data protection and privacy regulations (e.g., GDPR, CCPA)',
                        'Aviation authority regulations regarding cockpit recording'
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <i className="ri-arrow-right-s-line text-[#B4FF39] text-lg flex-shrink-0 mt-0.5"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="pt-2">
                      Users must obtain all necessary consents before recording audio or collecting data involving other individuals.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-500/10 border border-gray-500/30 flex-shrink-0">
                  <i className="ri-shield-cross-line text-gray-400 text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">8. No Warranty</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      <strong className="text-white">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.</strong> WingmanFix and its owners, developers, employees, contractors, and partners expressly disclaim all warranties, including but not limited to:
                    </p>
                    <ul className="list-none space-y-2 ml-6">
                      {[
                        'Warranties of merchantability',
                        'Fitness for a particular purpose',
                        'Non-infringement',
                        'Accuracy, reliability, or completeness of information',
                        'Uninterrupted or error-free operation',
                        'Compatibility with devices or operating systems',
                        'Security or data protection'
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <i className="ri-close-circle-line text-gray-500 text-lg flex-shrink-0 mt-0.5"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="pt-2">
                      WingmanFix makes no guarantee that the Service will meet your requirements or that defects will be corrected.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-500/10 border border-red-500/30 flex-shrink-0">
                  <i className="ri-shield-line text-red-400 text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">9. Limitation of Liability</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      <strong className="text-white">TO THE MAXIMUM EXTENT PERMITTED BY LAW, WINGMANFIX AND ITS OWNERS, DEVELOPERS, EMPLOYEES, CONTRACTORS, PARTNERS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY DAMAGES WHATSOEVER RESULTING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE.</strong>
                    </p>
                    <p>
                      This limitation applies to all damages, including but not limited to:
                    </p>
                    <ul className="list-none space-y-2 ml-6">
                      {[
                        'Aircraft accidents, incidents, or mishaps',
                        'Personal injury, death, or bodily harm',
                        'Property damage or destruction',
                        'Operational errors or violations of aviation regulations',
                        'Loss of data, flight records, or information',
                        'Service interruptions or unavailability',
                        'Inaccurate or incomplete information',
                        'Reliance on AI-generated outputs',
                        'Direct, indirect, incidental, consequential, or punitive damages',
                        'Lost profits, revenue, or business opportunities'
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <i className="ri-close-circle-line text-red-400 text-lg flex-shrink-0 mt-0.5"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="pt-2">
                      This limitation applies even if WingmanFix has been advised of the possibility of such damages and regardless of the legal theory (contract, tort, negligence, strict liability, or otherwise).
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 10 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00A3E0]/10 border border-[#00A3E0]/30 flex-shrink-0">
                  <i className="ri-hand-heart-line text-[#00A3E0] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">10. Indemnification</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      You agree to indemnify, defend, and hold harmless WingmanFix and its owners, developers, employees, contractors, partners, and affiliates from and against any and all claims, liabilities, damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising from:
                    </p>
                    <ul className="list-none space-y-2 ml-6">
                      {[
                        'Your use of the Service',
                        'Your operation of an aircraft',
                        'Your violation of these Terms',
                        'Your violation of any law or regulation',
                        'Your violation of the rights of any third party',
                        'Any accident, incident, or injury related to your flight operations'
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <i className="ri-arrow-right-s-line text-[#00A3E0] text-lg flex-shrink-0 mt-0.5"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 11 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00FFD1]/10 border border-[#00FFD1]/30 flex-shrink-0">
                  <i className="ri-signal-wifi-error-line text-[#00FFD1] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">11. Service Availability</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      The Service may experience interruptions, connectivity limitations, device compatibility issues, software bugs, or periods of unavailability. WingmanFix does not guarantee continuous, uninterrupted, or secure access to the Service.
                    </p>
                    <p>
                      <strong className="text-white">Users must not rely on the Service as their sole source of operational information during flight.</strong> Always maintain backup systems, paper charts, and approved aircraft documentation.
                    </p>
                    <p>
                      WingmanFix reserves the right to modify, suspend, or discontinue the Service (or any part thereof) at any time without notice.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 12 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#B4FF39]/10 border border-[#B4FF39]/30 flex-shrink-0">
                  <i className="ri-user-follow-line text-[#B4FF39] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">12. User Conduct</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      By using the Service, you agree to:
                    </p>
                    <ul className="list-none space-y-2 ml-6">
                      {[
                        'Comply with all applicable federal, state, and local laws',
                        'Comply with all Federal Aviation Regulations (FARs)',
                        'Use the Service only for lawful purposes',
                        'Not use the Service in any manner that could damage, disable, or impair the Service',
                        'Not attempt to gain unauthorized access to any part of the Service',
                        'Not use the Service to violate the rights of others',
                        'Maintain responsibility for the safety of all flight operations'
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-line text-[#B4FF39] text-lg flex-shrink-0 mt-0.5"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 13 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00A3E0]/10 border border-[#00A3E0]/30 flex-shrink-0">
                  <i className="ri-scales-3-line text-[#00A3E0] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">13. Arbitration and Governing Law</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      These Terms shall be governed by and construed in accordance with the laws of the United States and the State of Delaware, without regard to its conflict of law provisions.
                    </p>
                    <p>
                      <strong className="text-white">Any dispute, claim, or controversy arising out of or relating to these Terms or the Service shall be resolved through binding arbitration</strong> administered by the American Arbitration Association (AAA) in accordance with its Commercial Arbitration Rules. The arbitration shall take place in Delaware, and judgment on the arbitration award may be entered in any court having jurisdiction.
                    </p>
                    <p>
                      You agree to waive any right to a jury trial or to participate in a class action lawsuit.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 14 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#00FFD1]/10 border border-[#00FFD1]/30 flex-shrink-0">
                  <i className="ri-refresh-line text-[#00FFD1] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">14. Changes to Terms</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      WingmanFix reserves the right to modify, update, or replace these Terms at any time without prior notice. Updated Terms will be posted on this page with a revised "Effective Date."
                    </p>
                    <p>
                      <strong className="text-white">Your continued use of the Service after any changes to these Terms constitutes your acceptance of the updated Terms.</strong> It is your responsibility to review these Terms periodically.
                    </p>
                    <p>
                      If you do not agree to the updated Terms, you must discontinue use of the Service immediately.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 15 */}
            <section>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#B4FF39]/10 border border-[#B4FF39]/30 flex-shrink-0">
                  <i className="ri-mail-line text-[#B4FF39] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">15. Contact Information</h2>
                  <div className="text-base text-gray-300 leading-relaxed space-y-3">
                    <p>
                      If you have any questions, concerns, or requests regarding these Terms of Use and Safety Disclaimer, please contact us at:
                    </p>
                    <div className="bg-[#0D1B2E] border border-[#B4FF39]/20 rounded-xl p-6 mt-4">
                      <p className="text-lg font-semibold text-white mb-2">WingmanFix Support</p>
                      <a 
                        href="mailto:support@wingmanfix.com" 
                        className="text-[#B4FF39] hover:text-[#c8ff5a] transition-colors inline-flex items-center gap-2"
                      >
                        <i className="ri-mail-line"></i>
                        support@wingmanfix.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Final Safety Notice */}
          <div className="mt-16 bg-gradient-to-br from-red-500/10 to-orange-500/10 border-2 border-red-500/30 rounded-2xl p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-red-500/20 flex-shrink-0">
                <i className="ri-alert-line text-red-400 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-3">Final Safety Reminder</h3>
                <p className="text-base text-gray-300 leading-relaxed">
                  <strong className="text-white">WingmanFix is a training and advisory tool only.</strong> The pilot in command is always responsible for the safe operation of the aircraft. Never rely solely on WingmanFix for operational decisions. Always verify information using approved aircraft documentation and certified systems. Fly safe.
                </p>
              </div>
            </div>
          </div>

          {/* Acknowledgment */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 leading-relaxed">
              By using WingmanFix, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use and Safety Disclaimer.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}